import React from 'react';
import cn from 'classnames';

interface Option {
  label: string;
  value: string;
}

interface Props
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  name: string;
  label: string;
  formik: any;
  options: Option[];
  required?: boolean;
  onValueChange?: (value: string) => void;
}

const FormikSelectField = ({
  name,
  label,
  formik,
  options,
  required,
  onValueChange,
  className = '',
  placeholder,
  disabled,
  ...rest
}: Props) => {
  const error = formik.touched[name] && formik.errors[name];
  const hasValue = formik.values[name] !== '';

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    formik.setFieldValue(name, value);
    onValueChange?.(value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    formik.handleBlur(e);
  };

  return (
    <div className="w-full mt-2 mb-1">
      {/* Label */}
      <label
        htmlFor={name}
        className="block mb-2 text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="ml-1 text-red-500"> * </span>}
      </label>

      {/* Select Wrapper */}
      <div className="relative">
        <select
          id={name}
          name={name}
          value={formik.values[name]}
          onChange={handleChange}
          onBlur={handleBlur}
          className={cn(
            'w-full px-3 py-2.5 pr-10 pl-3 text-sm border-[1px] border-gray-400 rounded-lg bg-white appearance-none cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2  focus:border-transparent',
            {
              'border-red-300 focus:ring-red-300': error,
              'text-gray-800': hasValue,
              'text-gray-500': !hasValue,
              'bg-gray-200  pointer-events-none opacity-50': disabled,
            },
            className,
          )}
          {...rest}
        >
          {/* Placeholder Option */}
          {placeholder && (
            <option
              value=""
              disabled={required}
              className="text-sm text-gray-400"
            >
              {placeholder}
            </option>
          )}

          {/* Options */}
          {options.map(option => (
            <option
              key={option.value}
              value={option.value}
              className="py-2 text-gray-900 border-b border-gray-100 last:border-b-0"
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Custom Arrow - Positioned on LEFT side */}
        <div className="absolute inset-y-0 flex items-center pointer-events-none left-3">
          <i className="icon-nameArrow---Down-2-1 ps-1"></i>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-1 text-sm text-red-600">{formik.errors[name]}</div>
      )}
    </div>
  );
};

export default FormikSelectField;
